import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useBranch } from "@/contexts/BranchContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { fmtIDR, fmtShortIDR, firstOfMonthISO, todayISO } from "@/lib/format";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, PieChart, Pie, Legend,
} from "recharts";
import { Tag, Lightbulb, TrendingUp, Users, Sparkles } from "lucide-react";

const COLORS = ["hsl(173 58% 39%)", "hsl(271 76% 53%)", "hsl(24 95% 53%)", "hsl(197 71% 52%)", "hsl(142 71% 45%)", "hsl(340 75% 55%)"];

export default function PromoAnalytics() {
  const { branchId } = useBranch();
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/promos/analytics", { params: { date_from: dateFrom, date_to: dateTo, branch_id: branchId } })
      .then((r) => setData(r.data));
  }, [branchId, dateFrom, dateTo]);

  const top = data?.top_promos || [];

  const genderData = useMemo(() => {
    if (!top.length) return [];
    return top.map((p) => ({
      name: p.code,
      "Wanita": p.gender?.F || 0,
      "Pria": p.gender?.M || 0,
    }));
  }, [top]);

  const newVsExistingData = useMemo(() => {
    if (!top.length) return [];
    return top.map((p) => ({
      name: p.code,
      "Pasien Baru": p.new_patient_uses || 0,
      "Existing": p.existing_patient_uses || 0,
    }));
  }, [top]);

  return (
    <div className="space-y-6" data-testid="promo-analytics-page">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl tracking-tight">Promo Analytics</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Analisa performa promo + saran berbasis psikologi & behavior pasien.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="text-xs text-muted-foreground">Dari</label>
            <Input type="date" data-testid="pa-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-[160px]" />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sampai</label>
            <Input type="date" data-testid="pa-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-[160px]" />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total Promo Tx</div>
          <div className="mt-1 font-heading text-2xl">{data?.total_promo_tx || 0}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue Promo</div>
          <div className="mt-1 font-heading text-2xl">{fmtShortIDR(data?.total_promo_revenue || 0)}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Promo Aktif</div>
          <div className="mt-1 font-heading text-2xl">{top.length}</div>
        </CardContent></Card>
        <Card><CardContent className="p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Saran Aktif</div>
          <div className="mt-1 font-heading text-2xl">{data?.suggestions?.length || 0}</div>
        </CardContent></Card>
      </div>

      {/* Top Ranked Promos */}
      <Card>
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <TrendingUp className="h-4 w-4" /> Top Ranked Promo
          </CardTitle>
          <CardDescription>Diurutkan berdasarkan jumlah penggunaan oleh pasien.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-3">
            {top.map((p, idx) => (
              <div key={p.promo_id} data-testid={`top-${p.code}`} className="rounded-lg border border-border p-4 relative overflow-hidden">
                <div className="absolute top-0 right-0 h-16 w-16 rounded-bl-full opacity-10" style={{ backgroundColor: COLORS[idx % COLORS.length] }} />
                <div className="flex items-center gap-2">
                  <span className="grid h-7 w-7 place-items-center rounded-full bg-foreground text-background text-xs font-bold">#{idx + 1}</span>
                  <code className="text-xs font-bold tracking-wider rounded bg-secondary px-2 py-0.5">{p.code}</code>
                </div>
                <div className="mt-2 font-heading text-base leading-tight">{p.name}</div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-muted-foreground">Uses</div>
                    <div className="font-semibold">{p.uses}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Revenue</div>
                    <div className="font-semibold">{fmtShortIDR(p.revenue)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Unik</div>
                    <div className="font-semibold">{p.unique_patients}</div>
                  </div>
                </div>
              </div>
            ))}
            {top.length === 0 && (
              <div className="md:col-span-4 text-center text-sm text-muted-foreground py-6">Belum ada data promo pada periode ini.</div>
            )}
          </div>
        </CardContent>
      </Card>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Users className="h-4 w-4" /> Demografi Gender per Promo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={genderData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Wanita" fill="hsl(340 75% 55%)" radius={[4, 4, 0, 0]} stackId="g" />
                  <Bar dataKey="Pria" fill="hsl(197 71% 52%)" radius={[4, 4, 0, 0]} stackId="g" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg">Pasien Baru vs Existing</CardTitle>
            <CardDescription>Pola adopsi: promo mana lebih dicintai pasien baru vs existing</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={newVsExistingData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="Pasien Baru" fill="hsl(142 71% 45%)" radius={[4, 4, 0, 0]} stackId="b" />
                  <Bar dataKey="Existing" fill="hsl(43 89% 50%)" radius={[4, 4, 0, 0]} stackId="b" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Suggestions panel */}
      <Card data-testid="suggestions-card">
        <CardHeader>
          <CardTitle className="font-heading text-lg flex items-center gap-2">
            <Lightbulb className="h-4 w-4" /> Saran Promo berbasis Psikologi & Behavior
          </CardTitle>
          <CardDescription>
            Rekomendasi management — berdasarkan karakteristik segmen pasien & prinsip behavioral economics.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {(data?.suggestions || []).map((s, idx) => (
            <div key={idx} data-testid={`suggestion-${idx}`} className="rounded-lg border border-border p-4 bg-card">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-amber-500" />
                    <div className="font-medium text-sm">{s.title}</div>
                  </div>
                  <div className="mt-1.5 text-xs text-muted-foreground">{s.segment}</div>
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Rekomendasi: </span>
                    <Badge variant="outline" className="ml-1 text-xs"><Tag className="h-3 w-3 mr-1" />{s.recommended_promo}</Badge>
                  </div>
                  <div className="mt-2 text-sm leading-relaxed">{s.rationale}</div>
                  <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-0.5 text-xs text-emerald-800">
                    <TrendingUp className="h-3 w-3" /> {s.expected_lift}
                  </div>
                </div>
              </div>
            </div>
          ))}
          {(data?.suggestions || []).length === 0 && (
            <div className="text-sm text-muted-foreground text-center py-6">Belum ada saran. Periode terlalu sempit?</div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
